import {
  assignPrototypeAPIs,
  assignTableAPIs,
  type CellData,
  type Column,
  type ColumnPinningPosition,
  type Header,
  type RowData,
  type Table,
  type TableFeature,
} from '@tanstack/react-table';
import {
  table_getCenterVisibleLeafColumns,
  table_getStartVisibleLeafColumns,
  table_getEndVisibleLeafColumns,
  column_getIsPinned,
  table_getCenterHeaderGroups,
  table_getCenterLeafColumns,
  table_getStartHeaderGroups,
  table_getStartLeafColumns,
  table_getEndHeaderGroups,
  table_getEndLeafColumns,
  table_getVisibleLeafColumns,
  table_setColumnPinning,
} from '@tanstack/react-table/static-functions';

import { isGroupColumnDef } from '../../hooks/useTable/datatable-typeguards.js';
import { getPrefixColumnWidth } from '../Layout/layout-utils.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import {
  createPrototypeConstructor,
  createTableConstructor,
} from '../types/table.types.js';
import { pinColumns } from './column-pinning-utils.js';

/**
 * Computes the left pinned position based on the widths of all preceding left pinned columns.
 * @internal
 */
function computeLeftPinnedPosition<TData extends RowData>(
  columnId: string,
  table: Table<TDataTableFeatures, TData>,
): number | undefined {
  const leftVisibleLeafColumns = table.getStartVisibleLeafColumns();
  const targetColumnIndex = leftVisibleLeafColumns.findIndex(
    (leaf) => leaf.id === columnId,
  );

  // column is either not pinned to the left or currently hidden
  if (targetColumnIndex < 0) {
    return undefined;
  }

  // the first pinned column, no need to calculate any offset
  if (targetColumnIndex === 0) {
    return 0;
  }

  const prevColumn = leftVisibleLeafColumns[targetColumnIndex - 1];
  const isPrefix = prevColumn.columnDef.meta?.builtinPlacement === 'prefix';

  const width = isPrefix
    ? // prefix columns have a fixed width
      // use that instead of measured header, as prefix columns are also pinned when headers are hidden
      getPrefixColumnWidth(
        prevColumn,
        table.atoms.visualVariantConfig?.get().rowDensity,
        table.options.rowThresholds,
      )
    : table.getColumnOrHeaderWidth(prevColumn.id);

  const prevPosition = prevColumn.getLeftPinnedPosition() ?? 0;

  // left pinned position of the preceding column plus the preceding column's width
  // is the left position for the current column
  return prevPosition + width;
}

/**
 * Computes the right pinned position based on the widths of all following right pinned columns.
 * @internal
 */
function computeRightPinnedPosition<TData extends RowData>(
  columnId: string,
  table: Table<TDataTableFeatures, TData>,
): number | undefined {
  const rightVisibleLeafColumns = table.getEndVisibleLeafColumns();
  const targetColumnIndex = rightVisibleLeafColumns.findIndex(
    (leaf) => leaf.id === columnId,
  );

  // column is either not pinned to the right or currently hidden
  if (targetColumnIndex < 0) {
    return undefined;
  }

  // the last pinned column, no need to calculate any offset
  if (targetColumnIndex === rightVisibleLeafColumns.length - 1) {
    return 0;
  }

  const nextColumn = rightVisibleLeafColumns[targetColumnIndex + 1];
  const nextPosition = nextColumn.getRightPinnedPosition() ?? 0;

  // right pinned position for the next column plus the next column's width
  // is the right position for the current column
  return nextPosition + table.getColumnOrHeaderWidth(nextColumn.id);
}

/**
 * Helper function to get the ordered header groups with left, center, and right pinned headers in the correct order.
 * @internal
 */
export function table_getOrderedHeaderGroups<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
): [
  Header<TDataTableFeatures, TData, unknown>[],
  Header<TDataTableFeatures, TData, unknown>[],
] {
  const leftGroups = table_getStartHeaderGroups(table);
  const centerGroups = table_getCenterHeaderGroups(table);
  const rightGroups = table_getEndHeaderGroups(table);

  const getOrderedHeaderGroup = (groupIndex: number) => [
    ...(leftGroups.at(groupIndex)?.headers ?? []),
    ...(centerGroups.at(groupIndex)?.headers ?? []),
    ...(rightGroups.at(groupIndex)?.headers ?? []),
  ];

  return [getOrderedHeaderGroup(0), getOrderedHeaderGroup(1)] as const;
}

/**
 * Helper function to determine if a header is pinned by checking the
 * pinned state of its column or, if it's a group header, the pinned state of its child headers.
 * @internal
 */
export function header_getIsPinned<
  TData extends RowData,
  TValue extends CellData = CellData,
>(header: Header<TDataTableFeatures, TData, TValue>): ColumnPinningPosition {
  // if this is a group header, determine its pinned state based on its subHeaders
  if (isGroupColumnDef(header.column.columnDef)) {
    // all sub headers have the same pinned state, so we can just target the first one
    const firstChild = header.subHeaders.at(0);
    if (firstChild) {
      return column_getIsPinned(firstChild.column);
    }
    return false;
  }
  return column_getIsPinned(header.column);
}

/**
 * Helper function to get the left pinned position of a given header
 * @internal
 */
export function header_getLeftPinnedPosition<
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  header: Header<TDataTableFeatures, TData, TValue>,
): number | undefined | false {
  // if this is a group header, determine its left position based on its leftmost subHeader
  if (isGroupColumnDef(header.column.columnDef)) {
    const firstChild = header.subHeaders.at(0);
    if (firstChild) {
      return column_getLeftPinnedPosition(firstChild.column);
    }
    return false;
  }
  return column_getLeftPinnedPosition(header.column);
}

/**
 * Helper function to get the right pinned position of a given header
 * @internal
 */
export function header_getRightPinnedPosition<
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  header: Header<TDataTableFeatures, TData, TValue>,
): number | undefined | false {
  // if this is a group header, determine its left position based on its rightmost subHeader
  if (isGroupColumnDef(header.column.columnDef)) {
    const lastChild = header.subHeaders.at(-1);
    if (lastChild) {
      return column_getRightPinnedPosition(lastChild.column);
    }
    return false;
  }
  return column_getRightPinnedPosition(header.column);
}

/**
 * Helper function to determine if a header is the last visible left pinned header.
 * @internal
 */
export function header_getIsLastVisibleLeftPinnedHeader<
  TData extends RowData,
  TValue extends CellData = CellData,
>(header: Header<TDataTableFeatures, TData, TValue>): boolean {
  if (isGroupColumnDef(header.column.columnDef)) {
    const lastChild = header.subHeaders.at(-1);
    if (lastChild) {
      return column_getIsLastVisibleLeftPinnedColumn(lastChild.column);
    }
    return false;
  }
  return column_getIsLastVisibleLeftPinnedColumn(header.column);
}

/**
 * Helper function to determine if a header is the first visible unpinned header.
 * @internal
 */
export function header_getIsFirstVisibleUnpinnedHeader<
  TData extends RowData,
  TValue extends CellData = CellData,
>(header: Header<TDataTableFeatures, TData, TValue>): boolean {
  if (isGroupColumnDef(header.column.columnDef)) {
    const firstChild = header.subHeaders.at(0);
    if (firstChild) {
      return column_getIsFirstVisibleUnpinnedColumn(firstChild.column);
    }
    return false;
  }
  return column_getIsFirstVisibleUnpinnedColumn(header.column);
}

/**
 * Helper function to determine if a header is the last visible unpinned header.
 * @internal
 */
export function header_getIsLastVisibleUnpinnedHeader<
  TData extends RowData,
  TValue extends CellData = CellData,
>(header: Header<TDataTableFeatures, TData, TValue>): boolean {
  if (isGroupColumnDef(header.column.columnDef)) {
    const lastChild = header.subHeaders.at(-1);
    if (lastChild) {
      return column_getIsLastVisibleUnpinnedColumn(lastChild.column);
    }
    return false;
  }
  return column_getIsLastVisibleUnpinnedColumn(header.column);
}

/**
 * Helper function to determine if a header is the first visible right pinned header.
 * @internal
 */
export function header_getIsFirstVisibleRightPinnedHeader<
  TData extends RowData,
  TValue extends CellData = CellData,
>(header: Header<TDataTableFeatures, TData, TValue>): boolean {
  if (isGroupColumnDef(header.column.columnDef)) {
    const firstChild = header.subHeaders.at(0);
    if (firstChild) {
      return column_getIsFirstVisibleRightPinnedColumn(firstChild.column);
    }

    return false;
  }
  return column_getIsFirstVisibleRightPinnedColumn(header.column);
}

export function header_pin<
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  header: Header<TDataTableFeatures, TData, TValue>,
  position: ColumnPinningPosition,
) {
  const { column, subHeaders: visibleSubHeaders } = header;
  const idsToPin: string[] = isGroupColumnDef(column.columnDef)
    ? visibleSubHeaders.map((subHeader) => subHeader.column.id)
    : [column.id];

  const columnPinningState = header.table.atoms.columnPinning.get();
  const pinningState = pinColumns(idsToPin, columnPinningState, position);

  table_setColumnPinning<TDataTableFeatures, TData>(
    header.column.table,
    pinningState,
  );
}

export function column_getLeftPinnedPosition<
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  column: Column<TDataTableFeatures, TData, TValue>,
  isResizingColumn?: boolean,
) {
  // prevent recalculation while resizing column to avoid jumpy behavior
  if (isResizingColumn) {
    column._cachedLeftPinnedPosition ??= computeLeftPinnedPosition(
      column.id,
      column.table,
    );

    return column._cachedLeftPinnedPosition;
  }

  column._cachedLeftPinnedPosition = undefined;
  return computeLeftPinnedPosition(column.id, column.table);
}

export function column_getRightPinnedPosition<
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  column: Column<TDataTableFeatures, TData, TValue>,
  isResizingColumn?: boolean,
) {
  // prevent recalculation while resizing column to avoid jumpy behavior
  if (isResizingColumn) {
    column._cachedRightPinnedPosition ??= computeRightPinnedPosition(
      column.id,
      column.table,
    );
    return column._cachedRightPinnedPosition;
  }
  column._cachedRightPinnedPosition = undefined;
  return computeRightPinnedPosition(column.id, column.table);
}

export function column_getIsLastVisibleLeftPinnedColumn<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>) {
  // return true if the current column or one of its child columns is the last visible left pinned column
  const lastLeftVisibleLeafColumnId = column.table
    .getStartVisibleLeafColumns()
    .at(-1)?.id;

  return (
    column.id === lastLeftVisibleLeafColumnId ||
    column.columns.some(
      (childColumn) => childColumn.id === lastLeftVisibleLeafColumnId,
    )
  );
}

/**
 * Helper function that returns true if the current column or one of its child columns is the first visible unpinned column.
 * @internal
 */
export function column_getIsFirstVisibleUnpinnedColumn<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>) {
  // return true if the current column or one of its child columns is the first visible, unpinned column
  const firstCenterVisibleLeafColumnId = column.table
    .getCenterVisibleLeafColumns()
    .at(0)?.id;

  return (
    column.id === firstCenterVisibleLeafColumnId ||
    column.columns.some(
      (childColumn) => childColumn.id === firstCenterVisibleLeafColumnId,
    )
  );
}

export function column_getIsLastVisibleUnpinnedColumn<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>) {
  // return true if the current column or one of its child columns is the last visible unpinned column
  const lastCenterVisibleLeafColumnId = column.table
    .getCenterVisibleLeafColumns()
    .at(-1)?.id;

  return (
    column.id === lastCenterVisibleLeafColumnId ||
    column.columns.some(
      (childColumn) => childColumn.id === lastCenterVisibleLeafColumnId,
    )
  );
}

export function column_getIsFirstVisibleRightPinnedColumn<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>) {
  // return true if the current column or one of its child columns is the first visible right pinned column
  const firstRightVisibleLeafColumnId = column.table
    .getEndVisibleLeafColumns()
    .at(0)?.id;

  return (
    column.id === firstRightVisibleLeafColumnId ||
    column.columns.some(
      (childColumn) => childColumn.id === firstRightVisibleLeafColumnId,
    )
  );
}

/**
 * Constructs the column pinning feature for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructColumnPinningFeature(): TableFeature {
  return {
    assignColumnPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs('columnPinningFeature', prototype, table, {
          column_getLeftPinnedPosition: {
            fn: (column, isResizingColumn) =>
              column_getLeftPinnedPosition(column, isResizingColumn),
            /*
             * Called once per rendered cell, so the dependencies have to be
             * O(1). The width version stands in for the measured widths of the
             * pinned columns: it changes whenever any measured width does,
             * which invalidates slightly more often than comparing the widths
             * themselves, but the computation it guards returns a number.
             */
            memoDeps: () => [
              table.atoms.columnResizing?.get()?.isResizingColumn,
              table.atoms.columnVisibility?.get(),
              table.atoms.columnPinning?.get()?.start,
              table._measuredColumnWidthsVersion ?? 0,
              table.options.columns,
            ],
          },
          column_getRightPinnedPosition: {
            fn: (column) => column_getRightPinnedPosition(column),
            memoDeps: () => [
              table.atoms.columnResizing?.get()?.isResizingColumn,
              table.atoms.columnVisibility?.get(),
              table.atoms.columnPinning?.get()?.end,
              table._measuredColumnWidthsVersion ?? 0,
              table.options.columns,
            ],
          },
          column_getIsLastVisibleLeftPinnedColumn: {
            fn: (column) => column_getIsLastVisibleLeftPinnedColumn(column),
            memoDeps: () => [
              table.atoms.columnPinning?.get()?.start,
              table.atoms.columnVisibility?.get(),
              table.options.columns,
            ],
          },
          column_getIsFirstVisibleUnpinnedColumn: {
            fn: (column) => column_getIsFirstVisibleUnpinnedColumn(column),
            memoDeps: () => [
              table.atoms.columnPinning?.get(),
              table.atoms.columnVisibility?.get(),
              table.atoms.columnOrder?.get(),
              table.options.columns,
            ],
          },
          column_getIsLastVisibleUnpinnedColumn: {
            fn: (column) => column_getIsLastVisibleUnpinnedColumn(column),
            memoDeps: () => [
              table.atoms.columnPinning?.get(),
              table.atoms.columnVisibility?.get(),
              table.atoms.columnOrder?.get(),
              table.options.columns,
            ],
          },
          column_getIsFirstVisibleRightPinnedColumn: {
            fn: (column) => column_getIsFirstVisibleRightPinnedColumn(column),
            memoDeps: () => [
              table.atoms.columnPinning?.get()?.end,
              table.atoms.columnVisibility?.get(),
              table.options.columns,
            ],
          },
        });
      },
    ),
    assignHeaderPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs('columnPinningFeature', prototype, table, {
          header_getIsPinned: { fn: (header) => header_getIsPinned(header) },
          header_getLeftPinnedPosition: {
            fn: (header) => header_getLeftPinnedPosition(header),
          },
          header_getRightPinnedPosition: {
            fn: (header) => header_getRightPinnedPosition(header),
          },
          header_getIsLastVisibleLeftPinnedHeader: {
            fn: (header) => header_getIsLastVisibleLeftPinnedHeader(header),
          },
          header_getIsFirstVisibleUnpinnedHeader: {
            fn: (header) => header_getIsFirstVisibleUnpinnedHeader(header),
          },
          header_getIsLastVisibleUnpinnedHeader: {
            fn: (header) => header_getIsLastVisibleUnpinnedHeader(header),
          },
          header_getIsFirstVisibleRightPinnedHeader: {
            fn: (header) => header_getIsFirstVisibleRightPinnedHeader(header),
          },
          header_pin: {
            fn: (header, position) => header_pin(header, position),
          },
        });
      },
    ),
    constructTableAPIs: createTableConstructor(
      (table: Table<TDataTableFeatures, RowData>) => {
        assignTableAPIs('columnPinningFeature', table, {
          table_getOrderedHeaderGroups: {
            fn: () => table_getOrderedHeaderGroups(table),
            memoDeps: () => [
              table.options.columns,
              table.atoms.columnVisibility?.get(),
              table.atoms.columnPinning?.get(),
              table.atoms.columnOrder?.get(),
            ],
          },
          // Redeclare these here to add the columnOrder state to the dependencies.
          table_getVisibleLeafColumns: {
            fn: () => table_getVisibleLeafColumns(table),
            memoDeps: () => [
              table.atoms.columnVisibility?.get(),
              table.atoms.columnOrder?.get(),
              table.options.columns,
            ],
          },
          // Redeclare these here to add the columnOrder state to the dependencies.
          table_getStartLeafColumns: {
            fn: () => table_getStartLeafColumns(table),
            memoDeps: () => [
              table.options.columns,
              table.atoms.columnPinning?.get(),
              table.atoms.columnOrder?.get(),
            ],
          },
          // Redeclare these here to add the columnOrder state to the dependencies.
          table_getEndLeafColumns: {
            fn: () => table_getEndLeafColumns(table),
            memoDeps: () => [
              table.options.columns,
              table.atoms.columnPinning?.get(),
              table.atoms.columnOrder?.get(),
            ],
          },
          // Redeclare these here to add the columnOrder state to the dependencies.
          table_getCenterLeafColumns: {
            fn: () => table_getCenterLeafColumns(table),
            memoDeps: () => [
              table.options.columns,
              table.atoms.columnPinning?.get(),
              table.atoms.columnOrder?.get(),
            ],
          },
          // Redeclare these here to add the columnOrder state to the dependencies.
          table_getStartVisibleLeafColumns: {
            fn: () => table_getStartVisibleLeafColumns(table),
            memoDeps: () => [
              table.options.columns,
              table.atoms.columnPinning?.get(),
              table.atoms.columnVisibility?.get(),
              table.atoms.columnOrder?.get(),
            ],
          },
          // Redeclare these here to add the columnOrder state to the dependencies.
          table_getCenterVisibleLeafColumns: {
            fn: () => table_getCenterVisibleLeafColumns(table),
            memoDeps: () => [
              table.options.columns,
              table.atoms.columnPinning?.get(),
              table.atoms.columnVisibility?.get(),
              table.atoms.columnOrder?.get(),
            ],
          },
          // Redeclare these here to add the columnOrder state to the dependencies.
          table_getEndVisibleLeafColumns: {
            fn: () => table_getEndVisibleLeafColumns(table),
            memoDeps: () => [
              table.options.columns,
              table.atoms.columnPinning?.get(),
              table.atoms.columnVisibility?.get(),
              table.atoms.columnOrder?.get(),
            ],
          },
        });
      },
    ),
  };
}

/**
 * Additional feature implementation for column pinning.
 * @internal
 */
export const DataTableColumnPinningFeature = constructColumnPinningFeature();
